import React, { useState } from 'react';
import { MenuItem, Popover } from '@mui/material';
import { Button } from 'flowbite-react';
import { IoChevronDown } from 'react-icons/io5';
import classNames from 'classnames';
import { useThemeContext } from 'src/context/ThemeProvider';
import { MenuOptionsType } from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import { useMarkAttendanceMutation } from 'src/hooks/utils/mutations/offlineDriveMutations/useMarkAttendanceMutation.hooks';
import { useToast } from 'src/context/ToastProvider';
import { OfflineDriveCandidate, OfflineDriveCandidateStatus } from 'types';
import AssignCompanyModal from '../../assignCompany/AssignCompanyModal';
import { OpportunityDrawer } from '../../viewOpportunity/OpportunityDrawer';
import { useSearchParams } from 'react-router-dom';
import VerifyDocumentsDrawer from '../VerifyDocuments/VerifyDocumentsDrawer';

interface Props {
	driveId: string;
	candidate: OfflineDriveCandidate;
}
const CandidateActions = ({ driveId, candidate }: Props) => {
	const { themeMode } = useThemeContext();
	const [searchParams] = useSearchParams();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const bulkSelect = searchParams.get('bulkSelect') === 'true';
	const id = open ? 'simple-popover' : undefined;
	const [openAssignCompanyModal, setOpenAssignCompanyModal] = useState(false);
	const [openVerifyDocumentsDrawer, setOpenVerifyDocumentsDrawer] = useState(false);
	const [openOpportunityDrawer, setOpenOpportunityDrawer] = useState(false);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};
	const markAttendanceMutation = useMarkAttendanceMutation();
	const toast = useToast();

	const canMarkOutOfDrive =
		!candidate.isPlaced &&
		[
			OfflineDriveCandidateStatus.IDLE,
			OfflineDriveCandidateStatus.ASSIGN,
			OfflineDriveCandidateStatus.BUSY,
			OfflineDriveCandidateStatus.PRESENT,
		].includes(candidate.status as OfflineDriveCandidateStatus);

	const menuOptions = [
		{
			key: 'mark-attendance-present',
			label: 'Mark Attendance - Present',
			hide: !(candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED) || candidate.isPlaced,
			onClick: () => {
				if (!driveId || !candidate?._id) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({ driveId, userIds: [candidate?._id], status: OfflineDriveCandidateStatus.IDLE });
			},
		},
		{
			key: 'assign-company',
			label: 'Assign Round',
			hide: ![OfflineDriveCandidateStatus.IDLE].includes(candidate.status as OfflineDriveCandidateStatus) || candidate.isPlaced,
			onClick: () => {
				setOpenAssignCompanyModal(true);
			},
		},
		{
			key: 'mark-out-of-drive',
			label: 'Mark out of drive',
			hide: !canMarkOutOfDrive,
			onClick: () => {
				if (!driveId || !candidate?._id) {
					toast({ title: 'Drive ID or User ID is missing' });
					return;
				}
				if (
					!window.confirm(
						'Mark this candidate as out of drive? They will not receive any new rounds or assignments for this drive. Existing applications stay as they are.',
					)
				) {
					return;
				}
				markAttendanceMutation.mutate({
					driveId,
					userIds: [candidate._id],
					status: OfflineDriveCandidateStatus.OUT_OF_DRIVE,
				});
			},
		},
		{
			key: 'return-to-drive',
			label: 'Return to drive (Idle)',
			hide: candidate.status !== OfflineDriveCandidateStatus.OUT_OF_DRIVE || candidate.isPlaced,
			onClick: () => {
				if (!driveId || !candidate?._id) {
					toast({ title: 'Drive ID or User ID is missing' });
					return;
				}
				if (
					!window.confirm(
						'Bring this candidate back into the drive? Their status will be Idle and they can be assigned rounds again.',
					)
				) {
					return;
				}
				markAttendanceMutation.mutate({
					driveId,
					userIds: [candidate._id],
					status: OfflineDriveCandidateStatus.IDLE,
					allowReenterFromOutOfDrive: true,
				});
			},
		},
		{
			key: 'mark-available',
			label: 'Mark Idle',
			hide:
				[
					OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED,
					OfflineDriveCandidateStatus.IDLE,
					OfflineDriveCandidateStatus.HIRED,
					OfflineDriveCandidateStatus.OUT_OF_DRIVE,
				].includes(candidate.status as OfflineDriveCandidateStatus) || candidate.isPlaced,
			onClick: () => {
				if (!driveId || !candidate?._id) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({ driveId, userIds: [candidate?._id], status: OfflineDriveCandidateStatus.IDLE });
			},
		},
		{
			key: 'mark-busy',
			label: 'Mark Busy',
			hide: !(candidate.status === OfflineDriveCandidateStatus.ASSIGN) || candidate.isPlaced,
			onClick: () => {
				if (!driveId || !candidate?._id) {
					toast({
						title: 'Drive ID or User ID is missing',
					});
					return;
				}
				markAttendanceMutation.mutate({
					driveId,
					userIds: [candidate?._id],
					status: OfflineDriveCandidateStatus.BUSY,
				});
			},
		},
		{
			key: 'view-opportunities',
			label: 'View Opportunities',
			hide: candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED,
			onClick: () => {
				setOpenOpportunityDrawer(true);
			},
		},
		{
			key: 'verify-documents',
			label: 'Verify Documents',
			hide: !candidate.status || candidate.isPlaced,
			onClick: () => {
				setOpenVerifyDocumentsDrawer(true);
			},
		},
		// {
		// 	key: 'reject',
		// 	label: 'Reject',
		// 	hide: candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED,
		// 	onClick: () => {
		// 		console.log('Reject');
		// 	},
		// },
		// {
		// 	key: 'send-offer',
		// 	label: 'Send Offer',
		// 	hide: candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED,
		// 	onClick: () => {
		// 		console.log('Send Offer');
		// 	},
		// },
	];

	const getMenuOptionsVisibility = (key: string | undefined) => {
		switch (key) {
			case 'mark-attendance-present':
				return candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED || !candidate.isPlaced
					? 'block'
					: 'none';
			case 'assign-company':
				return [OfflineDriveCandidateStatus.IDLE].includes(candidate.status) || !candidate.isPlaced ? 'block' : 'none';
			case 'mark-available':
				return [
					OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED,
					OfflineDriveCandidateStatus.IDLE,
					OfflineDriveCandidateStatus.HIRED,
				].includes(candidate.status) || candidate.isPlaced
					? 'none'
					: 'block';
			case 'view-opportunities':
				return candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED ? 'none' : 'block';
			case 'reject':
				return candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED ? 'none' : 'block';
			case 'send-offer':
				return candidate.status === OfflineDriveCandidateStatus.ATTENDANCE_NOT_MARKED ? 'none' : 'block';
			case 'mark-busy':
				return candidate.status === OfflineDriveCandidateStatus.ASSIGN ? 'block' : 'none';
			case 'verify-documents':
				return candidate.status ? 'block' : 'none';
		}
		return 'none';
	};

	return (
		<>
			<Button
				disabled={bulkSelect}
				color="purplePlaced"
				className="[&_span]:!p-0 !p-0 border-[#E5E7EB] whitespace-pre"
				onClick={handleClick}
			>
				<p className="px-[10px] py-[8px]">{candidate?.isPlaced ? 'Placed User' : 'Take Action'}</p>
				<IoChevronDown
					className={classNames('w-[34px] h-[34px] px-[8px] transition-transform', open ? 'rotate-[180deg]' : '')}
				/>
			</Button>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					'&>.MuiPaper-root': {
						borderRadius: '8px',
						background: themeMode === 'dark' ? '#18222D' : 'white',
						boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
					},
					'&>.MuiPaper-root>.MuiButtonBase-root': {
						minHeight: '40px',
					},
				}}
			>
				{menuOptions &&
					menuOptions
						?.filter(menuOption => !menuOption.hide)
						?.map((menuOption: MenuOptionsType, index: number) => (
							<MenuItem
								key={index}
								className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
								style={{
									padding: '8px 16px',
									// display: getMenuOptionsVisibility(menuOption.key || ''),
								}}
								onClick={event => {
									if (menuOption.onClick) {
										menuOption.onClick(event);
									}
									handleClose();
								}}
							>
								{menuOption.label}
							</MenuItem>
						))}
			</Popover>
			<AssignCompanyModal
				driveId={driveId}
				userIds={[candidate._id]}
				openAssignCompanyModal={openAssignCompanyModal}
				setOpenAssignCompanyModal={setOpenAssignCompanyModal}
			/>
			<OpportunityDrawer
				driveId={driveId}
				openOpportunityDrawer={openOpportunityDrawer}
				setOpenOpportunityDrawer={setOpenOpportunityDrawer}
				candidate={candidate}
			/>
			<VerifyDocumentsDrawer
				driveId={driveId}
				openVerifyDocumentsDrawer={openVerifyDocumentsDrawer}
				setOpenVerifyDocumentsDrawer={setOpenVerifyDocumentsDrawer}
				candidate={candidate}
			/>
		</>
	);
};

export default CandidateActions;
